import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MenuItem } from 'primeng/api';
import {
  CardapioMinishopCategoriaDTO,
  CardapioMinishopProdutoDTO,
} from '../../../../../models/cardapio-minishop/cardapio-minishop.model';

@Component({
  selector: 'app-cardapio-minishop-categoria-list',
  templateUrl: './cardapio-minishop-categoria-list.component.html',
  styleUrls: ['./cardapio-minishop-categoria-list.component.scss'],
})
export class CardapioMinishopCategoriaListComponent implements OnInit, OnDestroy {
  @Input() categorias: CardapioMinishopCategoriaDTO[] = [];
  @Input() termoBusca = '';
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';

  @Output() categoriaReordenada = new EventEmitter<{ prevIndex: number; currIndex: number }>();
  @Output() produtoReordenado = new EventEmitter<{ categoriaId: number; prevIndex: number; currIndex: number }>();
  @Output() editarProduto = new EventEmitter<CardapioMinishopProdutoDTO>();
  @Output() novoProduto = new EventEmitter<number>();
  @Output() editarCategoria = new EventEmitter<CardapioMinishopCategoriaDTO>();
  @Output() excluirCategoria = new EventEmitter<number>();
  @Output() toggleVisibilidadeProduto = new EventEmitter<CardapioMinishopProdutoDTO>();
  @Output() excluirProduto = new EventEmitter<number>();

  // Estado de edicao inline de categoria
  editandoCategoriaId: number | null = null;
  editandoCategoriaNome = '';

  // Menu de contexto
  categoriaMenuItems: MenuItem[] = [];
  categoriaMenuAtual: CardapioMinishopCategoriaDTO | null = null;

  texts = {
    produtos: '',
    produto: '',
    novo_produto: '',
    editar: '',
    excluir: '',
    sem_produtos: '',
  };

  private langChangeSub!: Subscription;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
  }

  // --- Filtro por busca ---

  filtrarProdutos(produtos: CardapioMinishopProdutoDTO[]): CardapioMinishopProdutoDTO[] {
    if (!this.termoBusca) return produtos;
    const termo = this.termoBusca.toLowerCase();
    return produtos.filter(p => p.nome.toLowerCase().includes(termo));
  }

  // --- Drag-and-drop de categorias ---

  onCategoriaDrop(event: CdkDragDrop<CardapioMinishopCategoriaDTO[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      this.categoriaReordenada.emit({
        prevIndex: event.previousIndex,
        currIndex: event.currentIndex,
      });
    }
  }

  // --- Drag-and-drop de produtos ---

  onProdutoDrop(event: CdkDragDrop<CardapioMinishopProdutoDTO[]>, categoriaId: number): void {
    if (event.previousIndex !== event.currentIndex) {
      this.produtoReordenado.emit({
        categoriaId,
        prevIndex: event.previousIndex,
        currIndex: event.currentIndex,
      });
    }
  }

  // --- Edicao inline de categoria ---

  iniciarEdicaoCategoria(categoria: CardapioMinishopCategoriaDTO): void {
    this.editandoCategoriaId = categoria.id!;
    this.editandoCategoriaNome = categoria.nome;
  }

  confirmarEdicaoCategoria(categoria: CardapioMinishopCategoriaDTO): void {
    if (this.editandoCategoriaNome.trim()) {
      categoria.nome = this.editandoCategoriaNome.trim();
      this.editarCategoria.emit(categoria);
    }
    this.editandoCategoriaId = null;
  }

  cancelarEdicaoCategoria(): void {
    this.editandoCategoriaId = null;
  }

  // --- Menu de contexto da categoria ---

  buildCategoriaMenu(categoria: CardapioMinishopCategoriaDTO): void {
    this.categoriaMenuAtual = categoria;
    this.categoriaMenuItems = [
      {
        label: this.texts.editar,
        icon: 'pi pi-pencil',
        command: () => this.iniciarEdicaoCategoria(categoria),
      },
      {
        separator: true,
      },
      {
        label: this.texts.excluir,
        icon: 'pi pi-trash',
        styleClass: 'text-red-500',
        command: () => this.excluirCategoria.emit(categoria.id!),
      },
    ];
  }

  // --- Helpers ---

  getContadorProdutos(categoria: CardapioMinishopCategoriaDTO): string {
    const count = categoria.produtos.length;
    return `${count} ${count === 1 ? this.texts.produto : this.texts.produtos}`;
  }

  trackByCategoria(index: number, item: CardapioMinishopCategoriaDTO): number {
    return item.id || index;
  }

  trackByProduto(index: number, item: CardapioMinishopProdutoDTO): number {
    return item.id || index;
  }

  private loadTranslations(): void {
    this.texts = {
      produtos: this.translate.instant('cardapio_minishop.categorias.produtos'),
      produto: this.translate.instant('cardapio_minishop.categorias.produto'),
      novo_produto: this.translate.instant('cardapio_minishop.categorias.novo_produto'),
      editar: this.translate.instant('cardapio_minishop.comum.editar'),
      excluir: this.translate.instant('cardapio_minishop.comum.excluir'),
      sem_produtos: this.translate.instant('cardapio_minishop.produto.sem_produtos'),
    };
  }
}
